import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaClock, FaLightbulb, FaCalculator } from 'react-icons/fa';

const SimplePendulumCalculator = () => {
  const [length, setLength] = useState('');
  const [period, setPeriod] = useState('');
  const [frequency, setFrequency] = useState('');
  const [angle, setAngle] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculatePendulum = () => {
    if (length) {
      const l = parseFloat(length);
      
      // Calculate period
      const T = 2 * Math.PI * Math.sqrt(l / 9.8);
      setPeriod(T.toFixed(2));

      // Calculate frequency
      const f = 1 / T;
      setFrequency(f.toFixed(4));

      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!length) return null;

    const l = parseFloat(length);
    const T = 2 * Math.PI * Math.sqrt(l / 9.8);

    // Generate data for 2 complete cycles
    const series = [
      {
        name: 'Pendulum Position',
        data: Array.from({ length: 100 }, (_, i) => {
          const t = (i / 99) * (2 * T); // Time from 0 to 2T
          const angle = 10 * Math.sin(2 * Math.PI * t / T); // Using 10° as max angle
          return [t, angle];
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Time (s)',
        },
      },
      yaxis: {
        title: {
          text: 'Angle (°)',
        },
      },
      title: {
        text: 'Pendulum Motion',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-yellow-100 to-orange-100">
      <Helmet>
        <title>Simple Pendulum Calculator | Physics Tool</title>
        <meta name="description" content="Calculate the period and frequency of a simple pendulum. Interactive calculator with motion visualization for understanding pendulum physics." />
        <meta name="keywords" content="simple pendulum calculator, pendulum period calculator, physics calculator, pendulum motion calculator, physics mechanics calculator" />
        <meta property="og:title" content="Simple Pendulum Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate the period and frequency of a simple pendulum. Interactive calculator with motion visualization for understanding pendulum physics." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Simple Pendulum Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate the period and frequency of a simple pendulum. Interactive calculator with motion visualization for understanding pendulum physics." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/simple-pendulum" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-yellow-600 mb-4">
            <FaClock />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Simple Pendulum Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Pendulum Period and Frequency</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Pendulum Length (m)</label>
                <input
                  type="number"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter length in meters"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Initial Angle (°)</label>
                <input
                  type="number"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter initial angle (optional)"
                />
              </div>
              <div>
                <button
                  onClick={calculatePendulum}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Pendulum Motion
                </button>
              </div>

              {period && (
                <div className="mt-4 p-4 bg-yellow-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-yellow-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-yellow-800">Results</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-yellow-800">Period: {period} s</p>
                        <p className="text-yellow-800">Frequency: {frequency} Hz</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the formula for simple pendulum period:
                      <br />
                      T = 2π × √(L/g)
                      <br />
                      <br />
                      Where:
                      <br />
                      T = Period
                      <br />
                      L = Length = {length} m
                      <br />
                      g = 9.8 m/s² (acceleration due to gravity)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Pendulum Motion</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows 2 complete cycles of pendulum motion
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the length of the pendulum in meters</li>
            <li>Optionally enter the initial angle (default is 10°)</li>
            <li>Click "Calculate Pendulum Motion" to get the results</li>
            <li>View the interactive graph showing the pendulum's motion</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Accurate Calculations</h3>
              <p>Calculate period and frequency using the simple pendulum formula</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Visualization</h3>
              <p>See how the pendulum's angle changes over time</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics students and educators</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is a Simple Pendulum?</h3>
              <p className="text-gray-600">
                A simple pendulum is a mass (bob) suspended from a fixed point by a string or rod. When displaced and released, it swings back and forth with a regular period.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What affects the period of a pendulum?</h3>
              <p className="text-gray-600">
                The period of a simple pendulum depends on:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Length of the pendulum (L)</li>
                  <li>Acceleration due to gravity (g)</li>
                  <li>(Note: Mass and amplitude have minimal effect for small angles)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows how the angle of the pendulum changes over time:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>X-axis represents time (seconds)</li>
                  <li>Y-axis represents angle (degrees)</li>
                  <li>Shows 2 complete cycles of motion</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Simple pendulums are used in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Pendulum clocks</li>
                  <li>Seismometers</li>
                  <li>Physics demonstrations</li>
                  <li>Engineering vibration analysis</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand pendulum motion concepts</li>
                  <li>Engineers analyze oscillatory systems</li>
                  <li>Teachers demonstrate simple harmonic motion</li>
                  <li>Anyone needing to calculate pendulum parameters</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimplePendulumCalculator;
